import get from 'lodash/get';
import {
	createResource,
	type RouterContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import type { FirstProject } from './getFirstProject.tsx';
import type { Properties as RequiredFrontendProperties } from './getRequiredFrontendProperties.tsx';
import type { Properties as RequiredProjectProperties } from './getRequiredProjectProperties.tsx';

const getKey = ({ match, query }: RouterContext) =>
	// projectKey from url path/query or browse/{issueKey}
	getProjectKeyId(match, query).projectKey || get(match, 'params.issueKey', '').split('-')[0];

export const requiredProjectProperties: RouteResource<RequiredProjectProperties> =
	createResource<RequiredProjectProperties>({
		type: 'SERVICEDESK_REQUIRED_PROJECT_PROPERTIES',
		getKey,
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-resource-servicedesk-queues-required-project-properties-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-nav-items" */ './getRequiredProjectProperties.tsx'
			),
		maxAge: 5 * 60 * 1000,
	});

const ONE_DAY_IN_MILLIS = 60 * 60 * 1000 * 24;

export const requiredFrontendProperties: RouteResource<RequiredFrontendProperties> =
	createResource<RequiredFrontendProperties>({
		type: 'SERVICEDESK_REQUIRED_FRONTEND_PROPERTIES',
		getKey,
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-resource-servicedesk-queues-properties-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-nav-items" */ './getRequiredFrontendProperties.tsx'
			),
		maxAge: ONE_DAY_IN_MILLIS,
	});

const TWELVE_HOURS_IN_MILLIS = 12 * 60 * 60 * 1000;

export const firstProjectResource: RouteResource<FirstProject> = createResource<FirstProject>({
	type: 'JSM_FIRST_PROJECT_RESOURCE',
	// first project is the same for the tenantId (there is also no per-project arg in request url). Therefore, only 1 key value is used.
	getKey: () => 'none',
	getDataLoader: () =>
		import(
			/* webpackChunkName: "async-resource-servicedesk-first-project-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-nav-items" */ './getFirstProject.tsx'
		),
	maxAge: TWELVE_HOURS_IN_MILLIS,
});

const TWENTY_FOUR_HOURS_IN_MILLIS = 24 * 60 * 60 * 1000;
const SIMPLIFIED_NAV_PROPERTY_KEY = 'jsm.isSimplifiedNav';

export const isSimplifiedNavResource: RouteResource<boolean> = createResource<boolean>({
	type: 'JSM_IS_SIMPLIFIED_NAV_RESOURCE',
	getKey: ({ match, query }: RouterContext) => {
		const { projectKey } = getProjectKeyId(match, query);
		return `${projectKey}___${SIMPLIFIED_NAV_PROPERTY_KEY}`;
	},
	getDataLoader: () =>
		import(
			/* webpackChunkName: "async-resource-servicedesk-is-simplified-nav-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-nav-items" */ './getIsSimplifiedNav.tsx'
		),
	maxAge: TWENTY_FOUR_HOURS_IN_MILLIS,
});
